import React, { useMemo } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import GridSearchTemplate from '@pages/GridSearchTemplate';
import RestrictionsPopup from '@components/RestrictionsPopup';
import { useTextResources, useMessageResources } from '@/language';
import { buildFilterFields } from './FiltersSection/FiltersSection';
import { buildColumns } from './GridSection/restrictionDetailsColumns';
import { useRestrictionDetailsPageStates, buildSearchSpec, validateSearch } from './RestrictionDetailsPage.hook';
import styles from './RestrictionDetailsPage.module.css';

const TEST_ID = 'restrictionDetails';

const RestrictionDetailsPage = () => {
    const text = useTextResources();
    const msg = useMessageResources();
    const {
        data,
        initial,
        hotelIds,
        roomTypeIds,
        rateIds,
        roomTypesSpec,
        ratesSpec,
        productsSpec,
        customersProps,
        popupHotelId,
        searchedFromDate,
        showRestrictionsPopup,
        setShowRestrictionsPopup,
        handleRestrictionsSaved,
        setDoSearchRef,
        setHotelIds,
        setRoomTypeIds,
        setRateIds,
        setChangeFieldRef,
        search,
        handleSearchComplete,
    } = useRestrictionDetailsPageStates();

    const title = text('Restrictions');

    const fields = useMemo(() => buildFilterFields({
        text,
        testId: `${TEST_ID}.filters`,
        hotelIds,
        roomTypeIds,
        rateIds,
        roomTypesSpec,
        ratesSpec,
        productsSpec,
        customersProps,
        setHotelIds,
        setRoomTypeIds,
        setRateIds,
    }), [text, hotelIds, roomTypeIds, rateIds, roomTypesSpec, ratesSpec, productsSpec, customersProps, setHotelIds, setRoomTypeIds, setRateIds]);

    const columns = useMemo(() => buildColumns({ text, testId: `${TEST_ID}.grid` }), [text]);

    const gridOptions = useMemo(() => ({
        search: {
            title,
            fields,
            initial,
            method: 'POST',
            api: search,
            buildSpec: buildSearchSpec,
            validate: (state) => validateSearch(state, msg),
            setChangeFieldRef,
            setDoSearchRef,
        },
        grid: {
            columns,
            cacheId: 'RestrictionDetails',
            paging: true,
            rowClassCallback: (row) => (row.IsActive ? '' : styles.inActive),
            customOptions: {
                sort: {
                    sorting: [
                        { columnName: 'Date', direction: 'asc' },
                        { columnName: 'CreateDate', direction: 'desc' },
                    ],
                },
            },
        },
    }), [title, fields, initial, search, msg, setChangeFieldRef, setDoSearchRef, columns]);

    return (
        <div style={{ display: 'flex', flex: 1, width: '100%', flexDirection: 'row' }} data-testId={TEST_ID}>
            <div style={{ marginRight: 8 }}>
                <Button.Group vertical icon style={{ float: 'left' }}>
                    <Popup
                        position='top right'
                        content={text('RestrictionPopup')}
                        trigger={
                            <Button icon onClick={() => setShowRestrictionsPopup(true)} data-testId={`${TEST_ID}.sidebar.restrictionsPopup`}>
                                <Icon size='big' className='icon-do-not-disturbe' />
                            </Button>
                        }
                    />
                </Button.Group>
            </div>
            <div style={{ flex: 1, display: 'flex', overflow: 'auto' }}>
                <GridSearchTemplate options={gridOptions} onSearchComplete={handleSearchComplete} data={data} />
            </div>
            {showRestrictionsPopup && (
                <RestrictionsPopup
                    HotelID={popupHotelId}
                    open={showRestrictionsPopup}
                    onClose={() => setShowRestrictionsPopup(false)}
                    onSave={handleRestrictionsSaved}
                    initialDate={searchedFromDate}
                />
            )}
        </div>
    );
};

export default RestrictionDetailsPage;
